import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Layers,
  LayoutTemplate,
  Loader2,
  Mail,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useBrandSettings } from "../../hooks/useBrandSettings";
import { usePreviewTemplateContext, useSuggestTemplateBundle } from "../../hooks/useAIWriter";
import { useServiceMenu } from "../../hooks/useServiceMenu";
import { useStaticPages } from "../../hooks/useStaticPages";
import type {
  BuilderMode,
  FixedPagePolicy,
  PriceUnit,
  ProposalTemplate,
  ProposalTemplateInsert,
  ProposalTemplateUpdate,
  ServiceItem,
  StaticPage,
} from "../../types";

type StepId = "basics" | "context" | "content" | "review";

type CustomTemplateService = {
  id: string;
  service_name: string;
  service_description: string;
  base_price: string;
  price_unit: PriceUnit;
};

const STEPS: { id: StepId; label: string; description: string }[] = [
  { id: "basics", label: "Basics", description: "Name and client type" },
  { id: "context", label: "AI context", description: "Voice and positioning" },
  { id: "content", label: "Content", description: "Services and pages" },
  { id: "review", label: "Review", description: "Save template" },
];


const TONE_OPTIONS = [
  "Professional and formal",
  "Warm and personal",
  "Confident and direct",
] as const;

const PRICE_UNIT_OPTIONS: { value: PriceUnit; label: string }[] = [
  { value: "per_clean", label: "Per clean" },
  { value: "per_hour", label: "Per hour" },
  { value: "per_sqft", label: "Per sqft" },
  { value: "per_visit", label: "Per visit" },
  { value: "fixed", label: "Fixed" },
];



interface TemplateWizardProps {
  onSubmit: (
    payload: ProposalTemplateInsert | ProposalTemplateUpdate
  ) => Promise<void> | void;
  isSubmitting?: boolean;
  onCancel?: () => void;
  mode?: "create" | "edit";
  initialTemplate?: ProposalTemplate | null;
}

function makeCustomService(): CustomTemplateService {
  return {
    id: crypto.randomUUID(),
    service_name: "",
    service_description: "",
    base_price: "",
    price_unit: "per_clean",
  };
}

function mapTemplateToCustomServices(
  template: ProposalTemplate | null | undefined
): CustomTemplateService[] {
  if (!template) return [];

  return (template.template_services ?? [])
    .filter((service) => !service.service_item_id)
    .map((service) => ({
      id: service.id || crypto.randomUUID(),
      service_name: service.service_name ?? "",
      service_description: service.service_description ?? "",
      base_price:
        typeof service.base_price === "number"
          ? String(service.base_price)
          : "",
      price_unit: service.price_unit ?? "per_clean",
    }));
}

function mapTemplateToSelectedServiceIds(
  template: ProposalTemplate | null | undefined
): string[] {
  if (!template) return [];

  return (template.template_services ?? [])
    .map((service) => service.service_item_id)
    .filter((id): id is string => typeof id === "string" && id.length > 0);
}

function mapTemplateToSelectedPageIds(
  template: ProposalTemplate | null | undefined
): string[] {
  if (!template) return [];

  return (template.template_static_pages ?? [])
    .map((page) => page.static_page_id)
    .filter((id): id is string => typeof id === "string" && id.length > 0);
}

function parseOptionalPrice(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

export function TemplateWizard({
  onSubmit,
  isSubmitting = false,
  onCancel,
  mode = "create",
  initialTemplate = null,
}: TemplateWizardProps) {
  const { data: brand } = useBrandSettings();
  const { data: serviceItems = [], isLoading: servicesLoading } =
    useServiceMenu(false);
  const { data: staticPages = [], isLoading: pagesLoading } = useStaticPages();

  const [step, setStep] = useState<StepId>("basics");

  const [name, setName] = useState("");
  const [clientType, setClientType] = useState("");
  const [clientTypeNotes, setClientTypeNotes] = useState("");
  const [builderMode, setBuilderMode] = useState<BuilderMode>("prebuilt");
  const fixedPagePolicy: FixedPagePolicy = "template_defined";
  const [description, setDescription] = useState("");
  const [aiTone, setAiTone] =
    useState<(typeof TONE_OPTIONS)[number]>("Warm and personal");
  const [aiIndustryContext, setAiIndustryContext] = useState("");
  const [aiServiceContext, setAiServiceContext] = useState("");
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [selectedPageIds, setSelectedPageIds] = useState<string[]>([]);
  const [customServices, setCustomServices] = useState<CustomTemplateService[]>([]);
  const [taxRatePercent, setTaxRatePercent] = useState("0");
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // AI context step state
  const [useAIContext, setUseAIContext] = useState(true);
  const [contextPreview, setContextPreview] = useState<string | null>(null);
  const previewTemplateContext = usePreviewTemplateContext();

  const suggestBundle = useSuggestTemplateBundle();
  const [bundleRationale, setBundleRationale] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== "edit" || !initialTemplate) {
      return;
    }

    setName(initialTemplate.name ?? "");
    setClientType(initialTemplate.client_type ?? "");
    setClientTypeNotes("");
    setBuilderMode(initialTemplate.builder_mode ?? "prebuilt");
    setDescription(initialTemplate.description ?? "");
    setAiTone(
      (initialTemplate.ai_tone as (typeof TONE_OPTIONS)[number] | null) ??
        "Warm and personal"
    );
    setAiIndustryContext(initialTemplate.ai_industry_context ?? "");
    setAiServiceContext(initialTemplate.ai_service_context ?? "");
    setSelectedServiceIds(mapTemplateToSelectedServiceIds(initialTemplate));
    setSelectedPageIds(mapTemplateToSelectedPageIds(initialTemplate));
    setCustomServices(mapTemplateToCustomServices(initialTemplate));
    setTaxRatePercent(
      String(
        typeof initialTemplate.tax_rate === "number"
          ? Number((initialTemplate.tax_rate * 100).toFixed(2))
          : 0
      )
    );
    setIsDefault(Boolean(initialTemplate.is_default));
    setError(null);
  }, [initialTemplate, mode]);

  const currentIndex = STEPS.findIndex((s) => s.id === step);

  const selectedServices = useMemo(
    () => serviceItems.filter((item) => selectedServiceIds.includes(item.id)),
    [serviceItems, selectedServiceIds]
  );

  const selectedPages = useMemo(
    () => staticPages.filter((page) => selectedPageIds.includes(page.id)),
    [staticPages, selectedPageIds]
  );

  const canContinueBasics = name.trim().length > 1;

  const toggleService = (itemId: string) => {
    setSelectedServiceIds((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const togglePage = (pageId: string) => {
    setSelectedPageIds((prev) =>
      prev.includes(pageId) ? prev.filter((id) => id !== pageId) : [...prev, pageId]
    );
  };

  const [expandedServiceId, setExpandedServiceId] = useState<string | null>(null);

  const addCustomService = () => {
    const newService = makeCustomService();
    setCustomServices((prev) => [...prev, newService]);
    setExpandedServiceId(newService.id);
  };

  const updateCustomService = (
    id: string,
    field: keyof Omit<CustomTemplateService, "id">,
    value: string
  ) => {
    setCustomServices((prev) =>
      prev.map((service) =>
        service.id === id ? { ...service, [field]: value } : service
      )
    );
  };

  const removeCustomService = (id: string) => {
    setCustomServices((prev) => prev.filter((service) => service.id !== id));
  };

  const clearSelections = () => {
    setSelectedServiceIds([]);
    setSelectedPageIds([]);
    setCustomServices([]);
  };

  const buildTemplateServices = (items: ServiceItem[]) =>
    items.map((item, index) => ({
      service_item_id: item.id,
      service_name: item.name,
      service_description: item.default_description ?? null,
      base_price: item.default_price ?? null,
      price_unit: item.price_unit ?? null,
      quantity: 1,
      sort_order: index,
    }));

  const buildCustomTemplateServices = (
    items: CustomTemplateService[],
    startIndex: number
  ) =>
    items
      .filter((item) => item.service_name.trim().length > 0)
      .map((item, index) => ({
        service_item_id: null,
        service_name: item.service_name.trim(),
        service_description: item.service_description.trim() || null,
        base_price: parseOptionalPrice(item.base_price),
        price_unit: item.price_unit,
        quantity: 1,
        sort_order: startIndex + index,
      }));

  const buildTemplatePages = (pages: StaticPage[]) =>
    pages.map((page, index) => ({
      static_page_id: page.id,
      title: page.title,
      content_html: page.content_html,
      page_order: page.page_order ?? index,
    }));

  const goNext = () => {
    if (step === "basics" && !canContinueBasics) {
      setError("Template name is required.");
      return;
    }

    setError(null);

    const next = STEPS[currentIndex + 1];
    if (next) setStep(next.id);
  };

  const goBack = () => {
    const prev = STEPS[currentIndex - 1];
    if (prev) setStep(prev.id);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setStep("basics");
      setError("Template name is required.");
      return;
    }

    const invalidCustomService = customServices.find(
      (service) =>
        service.service_name.trim().length === 0 &&
        (service.service_description.trim().length > 0 ||
          service.base_price.trim().length > 0)
    );

    if (invalidCustomService) {
      setStep("content");
      setError("Each custom template service needs a service name.");
      return;
    }

    const invalidCustomPrice = customServices.find(
      (service) =>
        service.base_price.trim().length > 0 &&
        parseOptionalPrice(service.base_price) === null
    );

    if (invalidCustomPrice) {
      setStep("content");
      setError("Each custom template service price must be a valid number.");
      return;
    }

    setError(null);

    const catalogTemplateServices = buildTemplateServices(selectedServices);
    const customTemplateServices = buildCustomTemplateServices(
      customServices,
      catalogTemplateServices.length
    );
    const allTemplateServices = [
      ...catalogTemplateServices,
      ...customTemplateServices,
    ];
    const allTemplatePages = buildTemplatePages(selectedPages);
    const normalizedTaxRate = Number(taxRatePercent || "0") / 100;

    const payload: ProposalTemplateInsert | ProposalTemplateUpdate = {
      name: name.trim(),
      category: clientType,
      client_type: clientType,
      builder_mode: builderMode,
      layout_version: 1,
      document_settings: {
        pageSize: "letter",
        pageOrientation: "portrait",
      },
      layout_json: { pages: [] },
      fixed_page_policy: fixedPagePolicy,
      description: description.trim() || null,
      ai_tone: useAIContext ? aiTone : null,
      ai_industry_context: aiIndustryContext.trim() || null,
      ai_service_context: aiServiceContext.trim() || null,
      tax_rate: normalizedTaxRate,
      default_tax_rate: normalizedTaxRate,
      is_default: isDefault,
      template_services: allTemplateServices,
      template_static_pages: allTemplatePages,
      default_line_items: allTemplateServices.map((item) => ({
        service_item_id: item.service_item_id,
        name: item.service_name,
        description: item.service_description,
        price: item.base_price,
        price_unit: item.price_unit,
        frequency: null,
        duration_hours: null,
        sort_order: item.sort_order,
      })),
      default_static_page_ids: selectedPageIds,
    };

    await onSubmit(payload);
  };

  const loading = servicesLoading || pagesLoading;
  const populatedCustomServices = customServices.filter(
    (service) => service.service_name.trim().length > 0
  );

  const isEditMode = mode === "edit";
  const pageTitle = isEditMode ? "Edit template" : "Create template";
  const pageDescription = isEditMode
    ? "Update this reusable template, including its builder mode, fixed-page policy, services, pages, AI context, and default tax settings."
    : "Build a reusable proposal blueprint on top of your company brand setup. Templates define the default services, pages, builder mode, fixed-page direction, tone, and tax settings used when creating new proposals.";
  const saveLabel = isEditMode ? "Save changes" : "Save template";

  return (
    <div className="min-h-screen bg-[#0f0f0f] text-white">
      <div className="mx-auto flex w-full max-w-7xl gap-10 px-6 py-10">
        <aside className="hidden w-72 shrink-0 xl:block">
          <div className="sticky top-24 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
            <p className="text-xs uppercase tracking-[0.14em] text-white/35">
              Template wizard
            </p>

            <div className="mt-5 space-y-3">
              {STEPS.map((item, idx) => {
                const isCurrent = item.id === step;
                const isComplete = idx < currentIndex;

                return (
                  <div
                    key={item.id}
                    className={cn(
                      "rounded-xl border px-4 py-3 transition-colors",
                      isCurrent && "border-amber-500/20 bg-amber-500/5",
                      isComplete && "border-emerald-500/20 bg-emerald-500/5",
                      !isCurrent &&
                        !isComplete &&
                        "border-white/10 bg-black/20 text-white/45"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "flex h-5 w-5 items-center justify-center rounded-full border text-[10px]",
                          isCurrent && "border-amber-400 text-amber-300",
                          isComplete && "border-emerald-400 text-emerald-300",
                          !isCurrent &&
                            !isComplete &&
                            "border-white/15 text-white/30"
                        )}
                      >
                        {isComplete ? <Check className="h-3 w-3" /> : idx + 1}
                      </span>
                      <p className="text-sm font-medium text-white/85">
                        {item.label}
                      </p>
                    </div>
                    <p className="mt-1 pl-7 text-xs text-white/40">
                      {item.description}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 rounded-xl border border-white/10 bg-black/20 p-4">
              <p className="text-xs uppercase tracking-[0.14em] text-white/30">
                Brand foundation
              </p>
              <p className="mt-2 text-sm text-white/65">
                Templates inherit your company setup from{" "}
                <span className="text-white/85">
                  {brand?.company_name || "Brand Setup"}
                </span>
                : logo, colors, reusable pages, and service catalog.
              </p>
            </div>
          </div>
        </aside>

        <main className="min-w-0 flex-1">
          <div className="mb-8">
            <p className="text-xs uppercase tracking-[0.14em] text-amber-400/80">
              Proposal templates
            </p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight">
              {pageTitle}
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-white/45">
              {pageDescription}
            </p>
          </div>

          {error && (
            <div className="mb-6 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex min-h-[320px] items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03]">
              <Loader2 className="h-5 w-5 animate-spin text-white/35" />
            </div>
          ) : (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              {step === "basics" && (
                <div className="space-y-6">
                  <SectionHeader
                    title="Template basics"
                    description="Start with the reusable identity of this template."
                  />

                  {/* Builder mode selection */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-white/75">
                      Proposal type <span className="ml-1 text-red-400">*</span>
                    </label>
                    <p className="text-xs text-white/40">
                      Choose how proposals from this template will be built. You can change this later.
                    </p>
                    <div className="grid gap-3 md:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => setBuilderMode("prebuilt")}
                        className={cn(
                          "flex flex-col items-start gap-2 rounded-xl border px-5 py-4 text-left transition-colors",
                          builderMode === "prebuilt"
                            ? "border-amber-500/30 bg-amber-500/10"
                            : "border-white/10 bg-black/20 hover:border-white/20 hover:bg-white/[0.03]"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <LayoutTemplate className={cn("h-5 w-5", builderMode === "prebuilt" ? "text-amber-400" : "text-white/40")} />
                          <span className="text-sm font-semibold text-white/90">Predefined proposal</span>
                          {builderMode === "prebuilt" && (
                            <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] text-amber-300">
                              Selected
                            </span>
                          )}
                        </div>
                        <p className="text-xs leading-5 text-white/50">
                          Standard structured layout — cover page, cover letter, services, and pages. No canvas work needed.
                        </p>
                      </button>

                      <button
                        type="button"
                        onClick={() => setBuilderMode("drag_drop")}
                        className={cn(
                          "flex flex-col items-start gap-2 rounded-xl border px-5 py-4 text-left transition-colors",
                          builderMode === "drag_drop"
                            ? "border-amber-500/30 bg-amber-500/10"
                            : "border-white/10 bg-black/20 hover:border-white/20 hover:bg-white/[0.03]"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <Layers className={cn("h-5 w-5", builderMode === "drag_drop" ? "text-amber-400" : "text-white/40")} />
                          <span className="text-sm font-semibold text-white/90">Custom builder</span>
                          {builderMode === "drag_drop" && (
                            <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] text-amber-300">
                              Selected
                            </span>
                          )}
                        </div>
                        <p className="text-xs leading-5 text-white/50">
                          Full drag-and-drop canvas control. Design every section, element, and layout for this template.
                        </p>
                      </button>
                    </div>
                  </div>

                  {/* Predefined proposal preview */}
                  {builderMode === "prebuilt" && (
                    <PrebuiltProposalPreview brand={brand} />
                  )}

                  <Field label="Template name" required>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Restaurant Daily Cleaning"
                      className={inputClass}
                    />
                  </Field>

                  <Field label="Client type" required>
                    <input
                      value={clientType}
                      onChange={(e) => setClientType(e.target.value)}
                      placeholder="e.g. Restaurant, Gym, Event Venue, HOA..."
                      className={inputClass}
                    />
                    <p className="mt-1.5 text-xs text-white/35">
                      Who this template is built for.
                    </p>
                  </Field>

                  <Field label="Client type notes">
                    <textarea
                      value={clientTypeNotes}
                      onChange={(e) => setClientTypeNotes(e.target.value)}
                      rows={3}
                      placeholder="Any specifics about this client type — scope, compliance needs, access requirements, frequency expectations..."
                      className={textareaClass}
                    />
                  </Field>

                  <Field label="Internal description">
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                      placeholder="A short internal note about when and how to use this template."
                      className={textareaClass}
                    />
                  </Field>
                </div>
              )}

              {step === "context" && (
                <div className="space-y-6">
                  <SectionHeader
                    title="AI context"
                    description="Define the tone and positioning that carries into proposals created from this template."
                  />

                  {/* AI on/off toggle */}
                  <div className="grid gap-3 md:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => { setUseAIContext(true); setContextPreview(null); }}
                      className={cn(
                        "flex items-start gap-3 rounded-xl border px-4 py-3 text-left transition-colors",
                        useAIContext
                          ? "border-amber-500/30 bg-amber-500/10"
                          : "border-white/10 bg-black/20 hover:border-white/20 hover:bg-white/[0.03]"
                      )}
                    >
                      <Sparkles className={cn("mt-0.5 h-4 w-4 shrink-0", useAIContext ? "text-amber-400" : "text-white/30")} />
                      <div>
                        <p className="text-sm font-medium text-white/90">Use AI assistance</p>
                        <p className="mt-0.5 text-xs leading-5 text-white/45">AI will use this context when writing proposals from this template.</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setUseAIContext(false); setContextPreview(null); }}
                      className={cn(
                        "flex items-start gap-3 rounded-xl border px-4 py-3 text-left transition-colors",
                        !useAIContext
                          ? "border-white/30 bg-white/5"
                          : "border-white/10 bg-black/20 hover:border-white/20 hover:bg-white/[0.03]"
                      )}
                    >
                      <Layers className={cn("mt-0.5 h-4 w-4 shrink-0", !useAIContext ? "text-white/70" : "text-white/30")} />
                      <div>
                        <p className="text-sm font-medium text-white/90">No AI context</p>
                        <p className="mt-0.5 text-xs leading-5 text-white/45">Skip this step. Proposals will still be created normally.</p>
                      </div>
                    </button>
                  </div>

                  {useAIContext && (
                    <>
                      {/* Tone */}
                      <Field label="Template tone">
                        <div className="grid gap-3 md:grid-cols-3">
                          {TONE_OPTIONS.map((option) => (
                            <button
                              key={option}
                              type="button"
                              onClick={() => { setAiTone(option); setContextPreview(null); }}
                              className={cn(
                                "rounded-xl border px-4 py-3 text-left text-sm transition-colors",
                                aiTone === option
                                  ? "border-amber-500/30 bg-amber-500/10 text-white"
                                  : "border-white/10 bg-black/20 text-white/60 hover:border-white/20 hover:text-white/80"
                              )}
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                      </Field>

                      {/* Industry context */}
                      <Field label="Industry context">
                        <textarea
                          value={aiIndustryContext}
                          onChange={(e) => { setAiIndustryContext(e.target.value); setContextPreview(null); }}
                          rows={3}
                          placeholder="This template is for independent restaurants that care deeply about guest experience, cleanliness, and health code compliance."
                          className={textareaClass}
                        />
                      </Field>

                      {/* Service context */}
                      <Field label="Service context">
                        <textarea
                          value={aiServiceContext}
                          onChange={(e) => { setAiServiceContext(e.target.value); setContextPreview(null); }}
                          rows={3}
                          placeholder="Default scope should emphasize FOH sanitization, BOH floors, restroom detail work, and consistent recurring service."
                          className={textareaClass}
                        />
                      </Field>

                      {/* Generate button */}
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          disabled={previewTemplateContext.isPending}
                          onClick={async () => {
                            setContextPreview(null);
                            const result = await previewTemplateContext.mutateAsync({
                              tone: aiTone,
                              industryContext: aiIndustryContext.trim() || undefined,
                              serviceContext: aiServiceContext.trim() || undefined,
                            });
                            setContextPreview(result.preview);
                          }}
                          className="flex items-center gap-2 rounded-xl bg-amber-500/15 px-4 py-2.5 text-sm font-medium text-amber-300 transition-colors hover:bg-amber-500/25 disabled:opacity-50"
                        >
                          {previewTemplateContext.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Sparkles className="h-4 w-4" />
                          )}
                          {previewTemplateContext.isPending ? "Generating…" : contextPreview ? "Regenerate" : "Generate preview"}
                        </button>
                        {previewTemplateContext.isError && (
                          <p className="text-xs text-red-400">Generation failed — check your connection and try again.</p>
                        )}
                      </div>

                      {/* Preview output */}
                      {contextPreview && (
                        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                          <div className="mb-2 flex items-center gap-2">
                            <Sparkles className="h-3.5 w-3.5 text-emerald-400" />
                            <p className="text-xs font-medium uppercase tracking-[0.12em] text-emerald-400">
                              AI writing sample — {aiTone}
                            </p>
                          </div>
                          <p className="text-sm leading-7 text-white/80">{contextPreview}</p>
                          <p className="mt-3 text-xs text-white/35">
                            This is how AI will sound when writing proposals from this template. Click Regenerate to get a different version, or Continue to accept it.
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {!useAIContext && (
                    <div className="space-y-5">
                      <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-3">
                        <p className="text-xs text-white/45">
                          Add your own context below. This is your words — it will appear as-is in proposals from this template. AI will not rewrite or reinterpret it.
                        </p>
                      </div>

                      <Field label="Industry context">
                        <textarea
                          value={aiIndustryContext}
                          onChange={(e) => setAiIndustryContext(e.target.value)}
                          rows={3}
                          placeholder="This template is for independent restaurants that care deeply about guest experience, cleanliness, and health code compliance."
                          className={textareaClass}
                        />
                      </Field>

                      <Field label="Service context">
                        <textarea
                          value={aiServiceContext}
                          onChange={(e) => setAiServiceContext(e.target.value)}
                          rows={3}
                          placeholder="Default scope should emphasize FOH sanitization, BOH floors, restroom detail work, and consistent recurring service."
                          className={textareaClass}
                        />
                      </Field>
                    </div>
                  )}
                </div>
              )}

              {step === "content" && (
                <div className="space-y-8">
                  <SectionHeader
                    title="Template content"
                    description="Choose Brand Setup services and pages, then add any template-specific services needed only for this template."
                  />

                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      {clientType.trim() && (serviceItems.length > 0 || staticPages.length > 0) && (
                        <button
                          type="button"
                          disabled={suggestBundle.isPending}
                          onClick={() => void (async () => {
                            setBundleRationale(null);
                            const result = await suggestBundle.mutateAsync({
                              clientType: clientType.trim(),
                              clientTypeNotes: clientTypeNotes.trim() || undefined,
                              availableServices: serviceItems.map((s) => ({
                                id: s.id,
                                name: s.name,
                                category: s.category ?? null,
                              })),
                              availablePages: staticPages.map((p) => ({
                                id: p.id,
                                title: p.title,
                                pageKind: p.page_kind ?? null,
                              })),
                            });
                            setSelectedServiceIds(result.suggestedServiceIds);
                            setSelectedPageIds(result.suggestedPageIds);
                            setBundleRationale(result.rationale);
                          })()}
                          className="flex items-center gap-2 rounded-xl bg-amber-500/15 px-4 py-2 text-sm font-medium text-amber-300 transition-colors hover:bg-amber-500/25 disabled:opacity-50"
                        >
                          {suggestBundle.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Sparkles className="h-4 w-4" />
                          )}
                          {suggestBundle.isPending ? "Suggesting…" : "Suggest for me"}
                        </button>
                      )}
                      {suggestBundle.isError && (
                        <p className="text-xs text-red-400">Suggestion failed — try again.</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => { clearSelections(); setBundleRationale(null); }}
                      className="rounded-xl border border-white/10 px-4 py-2 text-sm text-white/70 transition-colors hover:bg-white/[0.03]"
                    >
                      Clear all
                    </button>
                  </div>

                  {bundleRationale && (
                    <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3">
                      <div className="mb-1 flex items-center gap-2">
                        <Sparkles className="h-3.5 w-3.5 text-amber-400" />
                        <p className="text-xs font-medium uppercase tracking-[0.12em] text-amber-400">AI suggestion</p>
                      </div>
                      <p className="text-sm leading-6 text-white/75">{bundleRationale}</p>
                    </div>
                  )}

                  <Field label="Template services">
                    <div className="mb-2 flex items-center justify-between gap-4">
                      <p className="text-xs leading-5 text-white/45">
                        Select matching services from the company-wide Brand Setup catalog.
                      </p>
                      <p className="text-xs text-white/35">
                        Selected from catalog: {selectedServices.length}
                      </p>
                    </div>

                    <div className="space-y-2">
                      {serviceItems.length === 0 ? (
                        <EmptyNotice>
                          No services exist yet. Create and manage them in Brand Setup
                          before building template defaults.
                        </EmptyNotice>
                      ) : (
                        serviceItems.map((item) => (
                          <SelectableRow
                            key={item.id}
                            selected={selectedServiceIds.includes(item.id)}
                            onClick={() => toggleService(item.id)}
                            title={item.name}
                            meta={[
                              item.category || "General",
                              item.default_frequency || "No default frequency",
                              item.default_price != null
                                ? `$${item.default_price}`
                                : "No default price",
                            ]}
                          />
                        ))
                      )}
                    </div>
                  </Field>

                  <Field label="Template-specific services">
                    <div className="mb-3 flex items-center justify-between gap-4">
                      <p className="text-xs leading-5 text-white/45">
                        Add service rows that should exist for this template even if
                        they are not part of the global Brand Setup service catalog.
                      </p>
                      <button
                        type="button"
                        onClick={addCustomService}
                        className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-3 py-2 text-sm text-white/80 transition-colors hover:bg-white/[0.03]"
                      >
                        <Plus className="h-4 w-4" />
                        Add template service
                      </button>
                    </div>

                    {customServices.length === 0 ? (
                      <EmptyNotice>
                        No template-specific services yet. Add one when this template
                        needs services that should not live in the company-wide catalog.
                      </EmptyNotice>
                    ) : (
                      <div className="space-y-3">
                        {customServices.map((service, index) => {
                          const isExpanded = expandedServiceId === service.id;
                          const hasName = service.service_name.trim().length > 0;
                          return (
                            <div
                              key={service.id}
                              className="rounded-xl border border-white/10 bg-black/20"
                            >
                              {/* Header row — always visible */}
                              <div className="flex items-center justify-between gap-4 px-4 py-3">
                                <button
                                  type="button"
                                  onClick={() => setExpandedServiceId(isExpanded ? null : service.id)}
                                  className="flex min-w-0 flex-1 items-center gap-3 text-left"
                                >
                                  <Check className={cn("h-4 w-4 shrink-0", hasName ? "text-emerald-400" : "text-white/20")} />
                                  <div className="min-w-0">
                                    <p className={cn("truncate text-sm font-medium", hasName ? "text-white/85" : "text-white/35")}>
                                      {hasName ? service.service_name : `Template service ${index + 1}`}
                                    </p>
                                    {!isExpanded && hasName && (
                                      <p className="text-xs text-white/35">
                                        {service.base_price ? `$${service.base_price} · ` : ""}
                                        {PRICE_UNIT_OPTIONS.find((o) => o.value === service.price_unit)?.label ?? service.price_unit}
                                        {service.service_description ? ` · ${service.service_description.slice(0, 50)}${service.service_description.length > 50 ? "…" : ""}` : ""}
                                      </p>
                                    )}
                                    {!isExpanded && !hasName && (
                                      <p className="text-xs text-amber-400/70">Tap to fill in details</p>
                                    )}
                                  </div>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeCustomService(service.id)}
                                  className="shrink-0 rounded-lg p-2 text-white/25 transition-colors hover:bg-red-500/10 hover:text-red-400"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>

                              {/* Expanded form */}
                              {isExpanded && (
                                <div className="border-t border-white/10 px-4 pb-4 pt-4">
                                  <div className="grid gap-4 md:grid-cols-2">
                                    <div className="md:col-span-2">
                                      <Field label="Service name" required>
                                        <input
                                          value={service.service_name}
                                          onChange={(e) => updateCustomService(service.id, "service_name", e.target.value)}
                                          placeholder="Quarterly dining room detail"
                                          className={inputClass}
                                        />
                                      </Field>
                                    </div>

                                    <div>
                                      <Field label="Base price">
                                        <input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          value={service.base_price}
                                          onChange={(e) => updateCustomService(service.id, "base_price", e.target.value)}
                                          placeholder="250.00"
                                          className={inputClass}
                                        />
                                      </Field>
                                    </div>

                                    <div>
                                      <Field label="Price unit">
                                        <select
                                          value={service.price_unit}
                                          onChange={(e) => updateCustomService(service.id, "price_unit", e.target.value as PriceUnit)}
                                          className={inputClass}
                                        >
                                          {PRICE_UNIT_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                              {option.label}
                                            </option>
                                          ))}
                                        </select>
                                      </Field>
                                    </div>

                                    <div className="md:col-span-2">
                                      <Field label="Service description">
                                        <textarea
                                          rows={3}
                                          value={service.service_description}
                                          onChange={(e) => updateCustomService(service.id, "service_description", e.target.value)}
                                          placeholder="Describe the specific scope this template should include by default."
                                          className={textareaClass}
                                        />
                                      </Field>
                                    </div>
                                  </div>

                                  <div className="mt-4 flex items-center justify-between gap-3">
                                    <p className="text-xs text-white/30">Changes are held in the wizard — saved when you finish the template.</p>
                                    <button
                                      type="button"
                                      disabled={!hasName}
                                      onClick={() => setExpandedServiceId(null)}
                                      className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500/15 px-3 py-1.5 text-sm font-medium text-emerald-300 transition-colors hover:bg-emerald-500/25 disabled:opacity-40"
                                    >
                                      <Check className="h-3.5 w-3.5" />
                                      Done
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Field>

                  <Field label="Template pages">
                    <div className="mb-2 flex items-center justify-between gap-4">
                      <p className="text-xs leading-5 text-white/45">
                        Select reusable pages from Brand Setup.
                      </p>
                      <p className="text-xs text-white/35">
                        Selected: {selectedPages.length}
                      </p>
                    </div>

                    <div className="space-y-2">
                      {staticPages.length === 0 ? (
                        <EmptyNotice>
                          No reusable pages exist yet. Create and manage them in Brand
                          Setup before adding template pages.
                        </EmptyNotice>
                      ) : (
                        staticPages.map((page) => (
                          <SelectableRow
                            key={page.id}
                            selected={selectedPageIds.includes(page.id)}
                            onClick={() => togglePage(page.id)}
                            title={page.title}
                            meta={[page.page_kind ?? "page"]}
                          />
                        ))
                      )}
                    </div>
                  </Field>

                  <Field label="Default tax rate (%)">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={taxRatePercent}
                      onChange={(e) => setTaxRatePercent(e.target.value)}
                      className={inputClass}
                    />
                  </Field>

                  <label className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/20 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={isDefault}
                      onChange={(e) => setIsDefault(e.target.checked)}
                      className="h-4 w-4 rounded border-white/20 bg-transparent"
                    />
                    <div>
                      <p className="text-sm font-medium text-white/85">
                        Make this the default template
                      </p>
                      <p className="text-xs text-white/40">
                        New proposals can start from this template automatically.
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {step === "review" && (
                <div className="space-y-8">
                  <SectionHeader
                    title="Review template"
                    description="Confirm the reusable blueprint before saving it."
                  />

                  <ReviewCard
                    title="Basics"
                    rows={[
                      ["Name", name || "—"],
                      ["Client type", clientType || "—"],
                      ["Client type notes", clientTypeNotes || "—"],
                      ["Description", description || "—"],
                      [
                        "Proposal type",
                        builderMode === "prebuilt"
                          ? "Predefined proposal"
                          : "Custom builder (drag & drop)",
                      ],
                    ]}
                  />


                  <ReviewCard
                    title="AI context"
                    rows={
                      useAIContext
                        ? [
                            ["AI assistance", "On"],
                            ["Tone", aiTone],
                            ["Industry context", aiIndustryContext || "—"],
                            ["Service context", aiServiceContext || "—"],
                            ["Preview generated", contextPreview ? "Yes" : "Not generated"],
                          ]
                        : [
                            ["AI assistance", "Off — your context used as written"],
                            ["Industry context", aiIndustryContext || "—"],
                            ["Service context", aiServiceContext || "—"],
                          ]
                    }
                  />

                  <ReviewCard
                    title="Defaults"
                    rows={[
                      [
                        "Catalog services",
                        selectedServices.length
                          ? selectedServices.map((s) => s.name).join(", ")
                          : "No catalog services selected",
                      ],
                      [
                        "Template-specific services",
                        populatedCustomServices.length
                          ? populatedCustomServices
                              .map((s) => s.service_name)
                              .join(", ")
                          : "No template-specific services",
                      ],
                      [
                        "Pages",
                        selectedPages.length
                          ? selectedPages.map((p) => p.title).join(", ")
                          : "No included pages",
                      ],
                      ["Tax rate", `${taxRatePercent || "0"}%`],
                      ["Default template", isDefault ? "Yes" : "No"],
                    ]}
                  />

                  <div className="rounded-xl border border-amber-500/15 bg-amber-500/5 p-4">
                    <div className="flex items-start gap-3">
                      <Sparkles className="mt-0.5 h-4 w-4 text-amber-400" />
                      <p className="text-sm leading-6 text-white/55">
                        This template will inherit your brand setup from{" "}
                        <span className="text-white/80">
                          {brand?.company_name || "your company"}
                        </span>
                        , then proposals created from it will inherit this
                        template’s services, pages, builder mode, AI context, and
                        fixed-page policy.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-8 flex items-center justify-between border-t border-white/10 pt-6">
                <div>
                  {onCancel ? (
                    <button
                      type="button"
                      onClick={onCancel}
                      className="text-sm text-white/35 transition-colors hover:text-white/60"
                    >
                      Cancel
                    </button>
                  ) : (
                    <span />
                  )}
                </div>

                <div className="flex items-center gap-3">
                  {currentIndex > 0 && (
                    <button
                      type="button"
                      onClick={goBack}
                      disabled={isSubmitting}
                      className="flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 text-sm text-white/70 transition-colors hover:bg-white/[0.03] disabled:opacity-50"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Back
                    </button>
                  )}

                  {step !== "review" ? (
                    <button
                      type="button"
                      onClick={goNext}
                      disabled={isSubmitting || (step === "basics" && !canContinueBasics)}
                      className="flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-white/90 disabled:opacity-50"
                    >
                      Continue
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void handleSubmit()}
                      disabled={isSubmitting}
                      className="flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-white/90 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                      {saveLabel}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function PrebuiltProposalPreview({
  brand,
}: {
  brand: { company_name?: string | null; logo_url?: string | null; primary_color?: string | null; accent_color?: string | null; background_color?: string | null; text_color?: string | null } | null | undefined;
}) {
  const primary = brand?.primary_color || "#111827";
  const accent = brand?.accent_color || "#FF6B00";
  const bg = brand?.background_color || "#FFFFFF";
  const text = brand?.text_color || "#111827";
  const company = brand?.company_name || "Your Company";
  const logoUrl = brand?.logo_url || null;

  const PAGES = ["About Us", "Terms & Conditions", "Process Overview"];
  const SERVICES = [
    { name: "Primary Service", freq: "Weekly", price: "$—" },
    { name: "Secondary Service", freq: "Monthly", price: "$—" },
    { name: "Add-on Service", freq: "As needed", price: "$—" },
  ];

  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-4">
      <div className="mb-3 flex items-center gap-2">
        <LayoutTemplate className="h-3.5 w-3.5 text-amber-400" />
        <p className="text-xs font-medium uppercase tracking-[0.14em] text-amber-400/80">
          Predefined proposal preview
        </p>
      </div>
      <p className="mb-4 text-xs leading-5 text-white/40">
        This is the standard proposal structure your template will generate. Brand colors, logo,
        and content fill in automatically from your Brand Setup.
      </p>

      {/* Mini document */}
      <div
        className="overflow-hidden rounded-xl border border-black/10 shadow-xl"
        style={{ background: bg, color: text, fontSize: 10 }}
      >
        {/* Cover header */}
        <div
          className="px-6 py-5"
          style={{
            background: `linear-gradient(135deg, ${primary} 0%, ${accent} 100%)`,
            color: "#fff",
          }}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={company}
              className="mb-2 h-8 w-auto max-w-[100px] object-contain drop-shadow"
            />
          ) : (
            <div className="mb-1 h-5 w-5 rounded-full bg-white/20" />
          )}
          <div className="mt-2 text-sm font-bold leading-tight">{company}</div>
          <div className="mt-1 text-[10px] text-white/70">Proposal · Prepared for Client Name</div>
          <div className="mt-3 flex gap-3 text-[9px] text-white/60">
            <span>📞 Contact info</span>
            <span>✉ Email address</span>
          </div>
        </div>

        <div className="space-y-4 px-6 py-5">
          {/* Cover letter section */}
          <div>
            <div
              className="mb-1 text-[9px] font-semibold uppercase tracking-widest"
              style={{ color: accent }}
            >
              Cover Letter
            </div>
            <div className="text-[10px] font-semibold" style={{ color: text }}>Introduction</div>
            <div className="mt-2 space-y-1">
              {[70, 100, 55].map((w, i) => (
                <div key={i} className="h-1.5 rounded-full bg-black/10" style={{ width: `${w}%` }} />
              ))}
            </div>
          </div>

          {/* Services section */}
          <div>
            <div
              className="mb-2 text-[9px] font-semibold uppercase tracking-widest"
              style={{ color: accent }}
            >
              Services &amp; Pricing
            </div>
            <div className="overflow-hidden rounded-lg border border-black/10">
              <div
                className="grid grid-cols-3 px-3 py-1.5 text-[9px] font-semibold text-white"
                style={{ background: primary }}
              >
                <span>Service</span>
                <span>Frequency</span>
                <span className="text-right">Price</span>
              </div>
              {SERVICES.map((s) => (
                <div
                  key={s.name}
                  className="grid grid-cols-3 border-t border-black/5 px-3 py-1.5 text-[9px]"
                  style={{ color: text }}
                >
                  <span className="font-medium">{s.name}</span>
                  <span className="text-black/40">{s.freq}</span>
                  <span className="text-right text-black/40">{s.price}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Static pages section */}
          <div>
            <div
              className="mb-2 text-[9px] font-semibold uppercase tracking-widest"
              style={{ color: accent }}
            >
              Included Pages
            </div>
            <div className="flex flex-wrap gap-1.5">
              {PAGES.map((p) => (
                <span
                  key={p}
                  className="rounded-full border border-black/10 px-2 py-0.5 text-[9px]"
                  style={{ color: text }}
                >
                  {p}
                </span>
              ))}
            </div>
          </div>

          {/* Signature block */}
          <div className="flex items-center gap-3 border-t border-black/10 pt-3">
            <Mail className="h-3 w-3 text-black/25" />
            <div className="space-y-0.5">
              <div className="h-1.5 w-20 rounded-full bg-black/10" />
              <div className="h-1.5 w-28 rounded-full bg-black/10" />
            </div>
            <div
              className="ml-auto rounded px-2 py-1 text-[9px] font-semibold text-white"
              style={{ background: accent }}
            >
              Accept &amp; Sign
            </div>
          </div>
        </div>
      </div>

      <p className="mt-3 text-[10px] leading-5 text-white/30">
        Structure is fixed. Brand colors, logo, and content pull from your Brand Setup automatically.
        Services and pages you choose in the next steps fill in the template.
      </p>
    </div>
  );
}

function SectionHeader({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div>
      <h2 className="text-xl font-semibold text-white">{title}</h2>
      <p className="mt-1 text-sm leading-6 text-white/45">{description}</p>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-white/75">
        {label}
        {required && <span className="ml-1 text-red-400">*</span>}
      </label>
      {children}
    </div>
  );
}

function SelectableRow({
  selected,
  onClick,
  title,
  meta,
  badge,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  meta: string[];
  badge?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-start justify-between rounded-xl border px-4 py-3 text-left transition-colors",
        selected
          ? "border-emerald-500/25 bg-emerald-500/10"
          : "border-white/10 bg-black/20 hover:border-white/20 hover:bg-white/[0.03]"
      )}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-white/85">{title}</p>
          {badge ? (
            <span className="rounded-full border border-amber-400/20 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] text-amber-300">
              {badge}
            </span>
          ) : null}
        </div>
        <p className="mt-1 text-xs leading-5 text-white/40">{meta.join(" · ")}</p>
      </div>

      <span
        className={cn(
          "ml-4 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
          selected
            ? "border-emerald-400 bg-emerald-500 text-white"
            : "border-white/15 text-transparent"
        )}
      >
        <Check className="h-3 w-3" />
      </span>
    </button>
  );
}

function ReviewCard({
  title,
  rows,
}: {
  title: string;
  rows: Array<[string, string]>;
}) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 p-5">
      <p className="text-sm font-medium text-white/85">{title}</p>

      <div className="mt-4 space-y-3">
        {rows.map(([label, value]) => (
          <div key={label}>
            <p className="text-[11px] uppercase tracking-[0.14em] text-white/30">
              {label}
            </p>
            <p className="mt-1 text-sm leading-6 text-white/65">{value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmptyNotice({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-white/10 bg-black/20 px-4 py-5 text-sm text-white/40">
      {children}
    </div>
  );
}

const inputClass =
  "w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder:text-white/25 focus:border-white/20 focus:outline-none";

const textareaClass =
  "w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder:text-white/25 focus:border-white/20 focus:outline-none";